import { type ReactNode, type RefObject, useRef, useState } from 'react';

import { Link } from 'react-router';
import Slider from 'react-slick';

import { useAppDispatch, useAppSelector } from 'dux/app/hooks';

import { Box, Text, theme } from '@prose-ui';
import { styled } from '@prose-ui/legacy';
import { mediaMinWidth } from '@prose-ui/utils/media';
import { useNestedScrollControl } from 'hooks/useNestedScrollControl';

import CollapsibleWithEllipsis from 'Components/CollapsibleWithEllipsis';
import type { StaticImageData } from 'Components/Image';
import { Image } from 'Components/Image';
import { ProductPriceModule } from 'Components/ProductPriceModule';
import Spacer from 'Components/Spacer';

import { productsCategories, type productsSlugs } from 'constants/products';

import ProductCTA from 'Containers/ProductCTA';

import checkSvg from 'assets/images/productPages/scalpSerum/check-hero.svg';
import arrowLeftIcon from 'assets/images/productPages/scalpSerum/left.svg';
import arrowRightIcon from 'assets/images/productPages/scalpSerum/right.svg';

import useResponsiveVariant from 'utils/useResponsiveVariant';

import { trackHeapEvent } from 'dux/tracking/actions';
import { getHasOrdersByCategory } from 'dux/user/selectors';

import { AccordionBlock, type AccordionContent } from './AccordionBlock';

type ProductCardMobileProps = {
  src: { mobile: StaticImageData; desktop: StaticImageData };
  alt: string;
  title?: string;
  subtitle?: string;
};

type PersonalizationWorksBetterType = {
  title: ReactNode;
  description: string;
  disclaimer: string;
  reassurances: string[];
};

type PersonalizationWorksBetterProps = {
  personalizationWorksBetter: PersonalizationWorksBetterType;
};

type ProductHeroProps = {
  accordion: AccordionContent;
  productSlug: (typeof productsSlugs)[keyof typeof productsSlugs];
  slider: ProductCardMobileProps[];
  title: string;
  subtitle: string;
  description: string;
  productCategory: typeof productsCategories.HAIRCARE | typeof productsCategories.SKINCARE;
  heroRef: (node?: Element | null | undefined) => void;
  parentRef: RefObject<HTMLDivElement>;
  onScrollToReviews: () => void;
  personalizationWorksBetter: PersonalizationWorksBetterType;
  upsellModule: {
    title: string;
    description: JSX.Element;
    image: { src: StaticImageData; alt: string };
    link: { text: string; to: string };
  };
  isNew: boolean;
};

const StyledSlider = styled(Slider)`
  width: 100%;

  .slick-track {
    padding-top: ${theme.spacing['8x']};
  }

  .slick-dots {
    position: initial;

    display: flex !important;
    gap: ${theme.spacing['2x']};

    width: max-content;
    margin: 0 auto;
    padding-top: ${theme.spacing['3x']};
    padding-bottom: ${theme.spacing['10x']};

    li {
      width: 100%;
      height: 100%;
      margin: 0;
    }
  }
`;

const buttonArrowStyle = `
  width: 14px;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  background: transparent;
  z-index: 1;
  border: none;
  bottom: 24px;
  `;

/**
 * Positioning : to use the arrows you should provide a left, right and top value (leftAndRight, top)
 * these value are unique to the Slider its implemented in
 * Require to make it work : the container the slider should be position relative
 */

const ArrowButton = styled.button<{ direction: ArrowProps['direction'] }>`
  position: absolute;
  right: ${({ direction }) => (direction === 'next' ? '24px' : 'auto')};
  left: ${({ direction }) => (direction === 'next' ? 'auto' : '24px')};

  ${buttonArrowStyle};
`;

type ArrowProps = {
  direction: 'prev' | 'next';
  onClick: () => void;
  dataFrom?: string;
};

const SliderArrow = ({ direction, onClick, dataFrom }: ArrowProps) => (
  <ArrowButton
    data-click="change-slide-arrow"
    data-from={dataFrom}
    direction={direction}
    onClick={onClick}
  >
    <Image
      alt="go-next"
      height={14}
      src={direction === 'prev' ? arrowLeftIcon : arrowRightIcon}
      width={7}
    />
  </ArrowButton>
);

const StyledSliderDesktop = styled(Slider)`
  width: 100%;
  height: 100%;

  .slick-list {
    height: 100% !important;
  }

  .slick-track {
    height: 100%;
  }

  .slick-slide div {
    height: 100%;
  }

  .slick-dots {
    bottom: 0;
    padding-bottom: ${theme.spacing['6x']};

    li {
      width: 6px;
      height: 6px;
      margin: 0;
      padding: 0 6px;
    }
  }
`;

const Dot = styled('div')<{ currentSlide: number; index: number }>`
  width: 6px;
  height: 6px;
  border-radius: ${theme.radius.lg};

  opacity: ${(props) => (props.currentSlide === props.index ? '1' : '0.4')};
  background: ${theme.colors.primary[300]};

  ${mediaMinWidth('md')} {
    background: white;
  }
`;

const StyledImage = styled(Image)`
  overflow: hidden;

  width: 100%;
  height: 380px;
  border-radius: ${theme.radius.xs};

  object-fit: cover;

  ${mediaMinWidth('sm')} {
    height: 100%;
  }
`;

const SliderCardContainer = styled('div')`
  position: relative;
  margin: 0 ${theme.spacing['2x']};
`;

const SliderDesktopCardContainer = styled('div')`
  position: relative;
  height: 100%;
`;

const StyledLink = styled.span`
  cursor: pointer;
  text-decoration: underline;

  &:hover {
    text-decoration: none;
  }
`;

type ButtonLinkProps = { dataClick?: string; dataFrom?: string; text: string; onClick: () => void };

const ButtonLink = ({ dataClick, dataFrom, text, onClick }: ButtonLinkProps) => {
  return (
    <StyledLink
      data-click={dataClick}
      data-from={dataFrom}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          onClick();
        }
      }}
      role="button"
      tabIndex={0}
    >
      {text}
    </StyledLink>
  );
};

const CarouselTextOverlayContainer = styled.div`
  position: absolute;
  bottom: 0;
  /* This is needed because we are overriding the height of react slick slider above .slick-slide div  */
  height: auto !important;
  padding: ${theme.spacing['6x']};
  border-bottom-right-radius: 10px;
  border-bottom-left-radius: 10px;

  background-color: #0006;

  ${mediaMinWidth('sm')} {
    padding: ${theme.spacing['10x']};
    padding-bottom: ${theme.spacing['14x']};
  }
`;

type CarouselTextOverlayProps = {
  title: string;
  subtitle: string;
};

const CarouselTextOverlay = ({ title, subtitle }: CarouselTextOverlayProps) => {
  const { isMobile } = useResponsiveVariant({ legacy: false });
  return (
    <CarouselTextOverlayContainer>
      <Text
        color="neutral.100"
        marginBottom="1x"
        variant={isMobile ? 'body2regular' : 'body1regular'}
      >
        {title}
      </Text>
      <Text color="neutral.100" variant={isMobile ? 'body6regular' : 'body4regular'}>
        {subtitle}
      </Text>
    </CarouselTextOverlayContainer>
  );
};

const ProductCardMobile = ({ src, alt, title, subtitle }: ProductCardMobileProps) => (
  <SliderCardContainer>
    <StyledImage alt={alt} src={src.mobile} />
    {title && subtitle && <CarouselTextOverlay subtitle={subtitle} title={title} />}
  </SliderCardContainer>
);

const ProductCardDesktop = ({ src, alt, title, subtitle }: ProductCardMobileProps) => {
  const { isMobile } = useResponsiveVariant({ legacy: false });

  return (
    <SliderDesktopCardContainer>
      <StyledImage alt={alt} src={isMobile ? src.mobile : src.desktop} />
      {title && subtitle && <CarouselTextOverlay subtitle={subtitle} title={title} />}
    </SliderDesktopCardContainer>
  );
};

const MobileSlider = ({ slider }: { slider: ProductHeroProps['slider'] }) => {
  const dispatch = useAppDispatch();
  const [currentSlide, setCurrentSlide] = useState(0);
  const slidesToShowValue = 1;

  const customPaging = (i: number) => (
    <Dot
      currentSlide={currentSlide}
      data-click="change-slide-dot"
      data-from="product-image-module-carousel"
      index={i}
    />
  );
  const cards = slider.map((product: ProductCardMobileProps) => {
    return (
      <ProductCardMobile
        key={product.alt}
        alt={product.alt}
        src={product.src}
        subtitle={product.subtitle}
        title={product.title}
      />
    );
  });

  return (
    <StyledSlider
      afterChange={(slide: number) => {
        dispatch(trackHeapEvent('Product Image Module Carousel Scroll'));
        setCurrentSlide(Math.round(slide));
      }}
      arrows={false}
      centerMode
      centerPadding="24px"
      customPaging={customPaging}
      dots
      infinite
      slidesToShow={slidesToShowValue}
    >
      {cards}
    </StyledSlider>
  );
};

const DesktopSlider = ({ slider }: { slider: ProductHeroProps['slider'] }) => {
  const dispatch = useAppDispatch();
  const [currentSlide, setCurrentSlide] = useState(0);
  const sliderRef = useRef<Slider>(null);

  const customPaging = (i: number) => (
    <Dot
      currentSlide={currentSlide}
      data-click="change-slide-dot"
      data-from="product-image-module-carousel"
      index={i}
    />
  );
  const cards = slider.map((product: ProductCardMobileProps) => {
    return (
      <ProductCardDesktop
        key={product.alt}
        alt={product.alt}
        src={product.src}
        subtitle={product.subtitle}
        title={product.title}
      />
    );
  });

  return (
    <Box height="full" position="relative">
      <SliderArrow
        dataFrom="product-image-module-carousel"
        direction="prev"
        onClick={() => sliderRef?.current?.slickPrev()}
      />
      <StyledSliderDesktop
        ref={sliderRef}
        afterChange={(slide: number) => {
          dispatch(trackHeapEvent('Product Image Module Carousel Scroll'));
          setCurrentSlide(Math.round(slide));
        }}
        arrows={false}
        customPaging={customPaging}
        dots
        focusOnSelect
        infinite
        slidesToShow={1}
      >
        {cards}
      </StyledSliderDesktop>
      <SliderArrow
        dataFrom="product-image-module-carousel"
        direction="next"
        onClick={() => sliderRef?.current?.slickNext()}
      />
    </Box>
  );
};

const Header = ({
  title,
  subtitle,
  description,
  onScrollToReviews,
  productSlug,
  heroRef,
  productCategory,
}: Omit<
  ProductHeroProps,
  'slider' | 'personalizationWorksBetter' | 'parentRef' | 'accordion' | 'upsellModule' | 'isNew'
>) => {
  const handleClick = () => {
    if (onScrollToReviews) onScrollToReviews();
  };
  const hasOrdersByCategory = useAppSelector(getHasOrdersByCategory);

  return (
    <>
      <Box
        alignItems="flex-start"
        display="flex"
        flexDirection="column"
        paddingTop={{ mobile: '4x', tablet: 'none', desktop: 'none' }}
        paddingX={{ mobile: '6x', tablet: 'none', desktop: 'none' }}
      >
        <Text as="h1" color="primary.300" variant="h3">
          {title}
        </Text>

        <Spacer size={16} />

        <Text color="primary.300" variant="body4regular">
          {description}{' '}
          <ButtonLink
            dataClick="read-reviews"
            dataFrom="product-module"
            onClick={handleClick}
            text="Read Reviews"
          />
        </Text>

        <Spacer size={24} />

        <Text color="primary.300" variant="body4regular">
          {subtitle}
        </Text>
        <Box paddingY="6x" width="full">
          <Box display={{ mobile: 'none', tablet: 'block', desktop: 'block' }}>
            <ProductPriceModule productSlug={productSlug} />
          </Box>
        </Box>

        <Box ref={heroRef} width="full">
          <ProductCTA
            dataFrom="product-module"
            fullWidth
            noMargin
            productCategory={productCategory}
            slug={productSlug}
            theme="roundedBlack"
          />
        </Box>

        {!hasOrdersByCategory.oneOf && (
          <Text
            color="neutral.800"
            display={{ mobile: 'none', tablet: 'block', desktop: 'block' }}
            paddingTop="4x"
            textAlign="center"
            variant="body3regular"
            w="full"
          >
            Love your first order (or it’s on us)
          </Text>
        )}
        <Box
          __height="1px"
          AlignSelf="center"
          bgColor="neutral.400"
          marginTop="8x"
          marginX="auto"
          w="full"
        />
      </Box>
    </>
  );
};

const PersonalizationWorksBetter = ({
  personalizationWorksBetter,
}: PersonalizationWorksBetterProps) => {
  const { title, description, reassurances, disclaimer } = personalizationWorksBetter;
  return (
    <Box
      alignItems="flex-start"
      display="flex"
      flexDirection="column"
      gap="4x"
      paddingX={{ mobile: '6x', tablet: 'none', desktop: 'none' }}
      paddingY={{ mobile: '6x', tablet: '8x', desktop: '8x' }}
    >
      <Text color="primary.300" variant="label2medium">
        {title}
      </Text>
      {/* TODO: Replace this component with CollapsibleWithEllipsis. This component will need to be updated to accept jsx as children and not only string/string[] */}
      <CollapsibleWithEllipsis clampedLines={3}>
        <Text color="primary.300" variant="body4regular">
          {description}
        </Text>
        <Text color="neutral.800" variant="body5regular">
          {disclaimer}
        </Text>
      </CollapsibleWithEllipsis>

      <Box alignItems="flex-start" display="flex" flexDirection="column" gap="2x">
        {reassurances.map((reassurance) => (
          <Box key={reassurance} alignItems="center" display="flex" gap="2x">
            <Box
              __borderRadius="50%"
              alignItems="center"
              bgColor="primary.200"
              display="flex"
              justifyContent="center"
              padding="1x"
            >
              {/* Alt attributes must remain empty in this case as the images are purely decorative */}
              <Image alt="" height="10" src={checkSvg} width="10" />
            </Box>
            <Text color="primary.300" variant="body4regular">
              {reassurance}
            </Text>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

const SliderBox = styled.div`
  position: relative;
  display: flex;
  width: 100%;

  ${mediaMinWidth('sm')} {
    width: 50%;
  }

  ${mediaMinWidth('md')} {
    gap: ${theme.spacing['4x']};
    width: 66%;
    padding-right: ${theme.spacing['8x']};
  }
`;

const InfoBox = styled.div`
  width: 100%;

  ${mediaMinWidth('sm')} {
    scrollbar-color: transparent transparent;
    scrollbar-width: thin;

    overflow-y: scroll;

    width: 50%;
    padding-left: ${theme.spacing['4x']};
  }

  ${mediaMinWidth('md')} {
    width: 34%;
    padding-top: ${theme.spacing['4x']};
    padding-left: ${theme.spacing['8x']};
  }
`;

const Container = styled(Box)`
  flex-direction: column;

  ${mediaMinWidth('sm')} {
    flex-direction: row;
    height: 80vh;
  }
`;

type UpsellModuleProps = {
  title: string;
  description: JSX.Element;
  image: { src: StaticImageData; alt: string };
  link: { text: string; to: string };
};

const UpsellLink = styled(Link)`
  text-decoration: underline;
  text-decoration-color: ${theme.colors.primary['300']} !important;
`;

const UpsellImage = styled.img`
  width: 100%;
  height: 100%;
  border-bottom-left-radius: 10px;
  object-fit: cover;
`;

const UpsellModule = ({ title, description, image, link }: UpsellModuleProps) => {
  return (
    <Box __border={`0.5px solid ${theme.colors.primary['200']}`} __borderRadius="10px" width="full">
      <Box
        __borderTopLeftRadius="10px"
        __borderTopRightRadius="10px"
        bgColor="primary.200"
        paddingY="3x"
      >
        <Text color="primary.400" textAlign="center" variant="label2regular">
          {title}
        </Text>
      </Box>
      <Box display="flex" flexDirection="row" justifyContent="space-between">
        <Box __flex={1} display="flex">
          <UpsellImage alt={image.alt} src={image.src.src} />
        </Box>
        <Box __flex={2} display="flex" flexDirection="column" padding="4x">
          <Text color="primary.300" variant="body3regular">
            {description}
          </Text>
          <Box paddingTop="1x">
            <UpsellLink data-click="discover-the-set" data-from="density-set-module" to={link.to}>
              <Text color="primary.300" variant="body3regular">
                {link.text}
              </Text>
            </UpsellLink>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

const NewBadge = styled.div`
  position: absolute;
  z-index: 1;
  top: 56px;
  left: 56px;

  display: flex;
  align-items: center;
  justify-content: center;

  width: 50px;
  height: 50px;
  border-radius: 50%;

  background-color: ${theme.colors.highlight[200]};

  ${mediaMinWidth('md')} {
    top: 24px;
    left: 32px;
  }
`;

export const ProductHero = ({
  slider,
  title,
  subtitle,
  description,
  personalizationWorksBetter,
  onScrollToReviews,
  heroRef,
  productSlug,
  parentRef,
  productCategory,
  accordion,
  upsellModule,
  isNew,
}: ProductHeroProps) => {
  const childRef = useRef<HTMLDivElement>(null);
  const { isMobile, isDesktop, isTablet } = useResponsiveVariant({ legacy: false });
  const hasOrdersByCategory = useAppSelector(getHasOrdersByCategory);

  useNestedScrollControl({ parentRef, childRef });

  return (
    <Container
      bgColor="neutral.100"
      display="flex"
      height="full"
      paddingX={{ mobile: 'none', tablet: '8x', desktop: '8x' }}
      paddingY={{ mobile: 'none', tablet: '8x', desktop: '8x' }}
      w="full"
    >
      <SliderBox>
        {isNew && (
          <NewBadge>
            <Text variant="label1regular">NEW</Text>
          </NewBadge>
        )}
        {isMobile && !isTablet && <MobileSlider slider={slider} />}
        {isDesktop && (
          <SliderCardContainer>
            <StyledImage alt={slider[0]!.alt} src={slider[0]!.src.mobile} />
          </SliderCardContainer>
        )}
        {(isTablet || isDesktop) && (
          <Box height="full" padding="none" w={{ mobile: 'half', tablet: 'full', desktop: 'half' }}>
            <DesktopSlider slider={slider.slice(1)} />
          </Box>
        )}
      </SliderBox>

      <InfoBox ref={childRef}>
        <Header
          description={description}
          heroRef={heroRef}
          onScrollToReviews={onScrollToReviews}
          productCategory={productCategory}
          productSlug={productSlug}
          subtitle={subtitle}
          title={title}
        />
        <PersonalizationWorksBetter personalizationWorksBetter={personalizationWorksBetter} />
        <Box
          display={{ mobile: 'block', tablet: 'none', desktop: 'none' }}
          padding="6x"
          width="full"
        >
          <ProductPriceModule productSlug={productSlug} />
          {!hasOrdersByCategory.oneOf && (
            <Text
              color="neutral.800"
              display={{ mobile: 'block', tablet: 'none', desktop: 'none' }}
              paddingTop="4x"
              textAlign="center"
              variant="body5regular"
              w="full"
            >
              Love your first order (or it’s on us)
            </Text>
          )}
        </Box>
        <AccordionBlock content={accordion} />
        <Box
          display="flex"
          justifyContent="center"
          paddingX={{ mobile: '6x', tablet: 'none', desktop: 'none' }}
          paddingY="8x"
        >
          <UpsellModule
            description={upsellModule.description}
            image={upsellModule.image}
            link={upsellModule.link}
            title={upsellModule.title}
          />
        </Box>
      </InfoBox>
    </Container>
  );
};
